/**
 * useAnalytics Hook
 *
 * Analytics data for charts and reports.
 */

import { useState, useEffect, useCallback } from 'react';
import { transactionService } from '@services/transactions';
import { db } from '@services/db';
import { dataEvents } from '@services/dataEvents';
import { formatMonthAxis, formatMonthYear } from '@/utils/format';

interface CategorySpending {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  amount: number;
  percentage: number;
  transactionCount: number;
  categoryIds?: string[];
  isUncategorized?: boolean;
}

interface MonthlyTrend {
  month: string;
  monthKey: string;
  fullLabel: string;
  income: number;
  expense: number;
  net: number;
  hasData: boolean;
}

interface IncomeItem {
  id: string;
  amount: number;
  description: string;
  accountName: string;
  date: Date;
  isAdjustment: boolean;
  percentage: number;
}

interface AnalyticsData {
  categorySpending: CategorySpending[];
  monthlyTrends: MonthlyTrend[];
  incomeBreakdown: IncomeItem[];  // Individual income transactions (excludes adjustments)
  totalIncome: number;            // Real income (excludes adjustments)
  totalExpense: number;           // Real expenses (excludes adjustments)
  adjustments: {                  // Separate tracking for balance adjustments
    income: number;               // Positive adjustments
    expense: number;              // Negative adjustments
    net: number;                  // Net adjustment
    count: number;                // Number of adjustment transactions
  };
  capitalMoves: {                 // Watchlist capital-move groups (excludeFromSpending)
    expense: number;              // Recorded as expense but really asset moves out
    income: number;               // Recorded as income but really asset moves back
    count: number;
  };
  transactionCount: number;        // Real income/expense transactions
  incomeCount: number;
  expenseCount: number;
  monthsCovered: number;
  // Smart-insight extras (computed in the same O(n) pass, adjustments excluded)
  largestExpense: { amount: number; description: string; date: Date } | null;
  weekendExpense: number;          // Expense total falling on Sat/Sun
  spendDays: number;               // Distinct local days with >=1 real expense
  activeDays: number;              // Days elapsed in range (capped at today)
}

interface UseAnalyticsResult {
  data: AnalyticsData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  dateRange: { start: Date; end: Date };
  setDateRange: (range: { start: Date; end: Date }) => void;
}

export interface UseAnalyticsOptions {
  /**
   * Descriptions matching this predicate are capital moves (asset
   * reallocation, e.g. funds moved to an external crypto wallet), NOT real
   * spending. They are excluded from totals/categories/trends and tracked in
   * data.capitalMoves. Pass a memoized function - it is a fetch dependency.
   */
  capitalMoveMatcher?: ((description: string) => boolean) | null;
}

/**
 * Get last N months date range.
 */
function getLastNMonthsRange(n: number): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const start = new Date(now.getFullYear(), now.getMonth() - n + 1, 1);
  return { start, end };
}

/**
 * Hook for analytics/chart data.
 */
export function useAnalytics(months = 6, options: UseAnalyticsOptions = {}): UseAnalyticsResult {
  const { capitalMoveMatcher = null } = options;
  const [dateRange, setDateRange] = useState(() => getLastNMonthsRange(months));
  const [data, setData] = useState<AnalyticsData>({
    categorySpending: [],
    monthlyTrends: [],
    incomeBreakdown: [],
    totalIncome: 0,
    totalExpense: 0,
    adjustments: { income: 0, expense: 0, net: 0, count: 0 },
    capitalMoves: { expense: 0, income: 0, count: 0 },
    transactionCount: 0,
    incomeCount: 0,
    expenseCount: 0,
    monthsCovered: 0,
    largestExpense: null,
    weekendExpense: 0,
    spendDays: 0,
    activeDays: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch transactions, categories, and accounts
      const [transactions, categories, accounts] = await Promise.all([
        transactionService.getByDateRange(dateRange.start, dateRange.end),
        db.categories.toArray(),
        db.accounts.toArray(),
      ]);

      const categoryMap = new Map(categories.map((c) => [c.id, c]));
      const accountMap = new Map(accounts.map((a) => [a.id, a.name || 'Unknown']));

      // Calculate category spending and collect income items
      // IMPORTANT: Adjustments are tracked separately, NOT included in totals
      const spendingMap = new Map<string, {
        categoryId: string;
        categoryName: string;
        categoryColor: string;
        amount: number;
        transactionCount: number;
        categoryIds: Set<string>;
        isUncategorized: boolean;
      }>();
      const incomeItems: IncomeItem[] = [];
      let totalExpense = 0;
      let totalIncome = 0;
      let adjustmentIncome = 0;
      let adjustmentExpense = 0;
      let adjustmentCount = 0;
      let incomeCount = 0;
      let expenseCount = 0;
      let largestExpense: { amount: number; description: string; date: Date } | null = null;
      let weekendExpense = 0;
      const spendDayKeys = new Set<string>();
      let capitalMoveExpense = 0;
      let capitalMoveIncome = 0;
      let capitalMoveCount = 0;

      const addExpenseToBucket = (categoryId: string | undefined, amount: number) => {
        const cat = categoryId ? categoryMap.get(categoryId) : undefined;
        const bucketKey = cat?.id || UNCATEGORIZED_CATEGORY_ID;

        if (!spendingMap.has(bucketKey)) {
          spendingMap.set(bucketKey, {
            categoryId: bucketKey,
            categoryName: cat?.name || 'Uncategorized',
            categoryColor: cat?.color || UNCATEGORIZED_COLOR,
            amount: 0,
            transactionCount: 0,
            categoryIds: new Set<string>(),
            isUncategorized: !cat,
          });
        }

        const bucket = spendingMap.get(bucketKey)!;
        bucket.amount += amount;
        bucket.transactionCount += 1;
        if (categoryId) {
          bucket.categoryIds.add(categoryId);
        }
      };

      for (const txn of transactions) {
        const txnIsAdjustment = isAdjustment(txn.description);

        // Capital moves (watchlist groups flagged excludeFromSpending):
        // asset reallocation, not real income/spending. Tracked separately;
        // balances and the transactions themselves are untouched.
        if (
          !txnIsAdjustment &&
          capitalMoveMatcher &&
          (txn.type === 'expense' || txn.type === 'income') &&
          capitalMoveMatcher(txn.description)
        ) {
          if (txn.type === 'expense') capitalMoveExpense += txn.amount;
          else capitalMoveIncome += txn.amount;
          capitalMoveCount++;
          continue;
        }

        if (txn.type === 'expense') {
          if (txnIsAdjustment) {
            // Track adjustment separately
            adjustmentExpense += txn.amount;
            adjustmentCount++;
          } else {
            // Real expense - include even when category is missing or orphaned.
            addExpenseToBucket(txn.categoryId, txn.amount);
            totalExpense += txn.amount;
            expenseCount++;

            const txnDate = normalizeDate(txn.date);
            if (!largestExpense || txn.amount > largestExpense.amount) {
              largestExpense = {
                amount: txn.amount,
                description: txn.description || '',
                date: txnDate,
              };
            }
            const dow = txnDate.getDay();
            if (dow === 0 || dow === 6) {
              weekendExpense += txn.amount;
            }
            spendDayKeys.add(
              `${txnDate.getFullYear()}-${txnDate.getMonth()}-${txnDate.getDate()}`
            );
          }
        } else if (txn.type === 'income') {
          if (txnIsAdjustment) {
            // Track adjustment separately
            adjustmentIncome += txn.amount;
            adjustmentCount++;
          } else {
            // Real income
            totalIncome += txn.amount;
            incomeCount++;
            // Collect for breakdown (only real income)
            incomeItems.push({
              id: txn.id!,
              amount: txn.amount,
              description: txn.description || '',
              accountName: accountMap.get(txn.accountId) || 'Unknown',
              date: txn.date instanceof Date ? txn.date : new Date(txn.date),
              isAdjustment: false,
              percentage: 0, // Will calculate after total is known
            });
          }
        }
      }

      // Calculate percentages for income items and sort by amount
      const incomeBreakdown = incomeItems
        .map(item => ({
          ...item,
          percentage: totalIncome > 0 ? (item.amount / totalIncome) * 100 : 0,
        }))
        .sort((a, b) => b.amount - a.amount);

      // Track adjustments separately
      const adjustments = {
        income: adjustmentIncome,
        expense: adjustmentExpense,
        net: adjustmentIncome - adjustmentExpense,
        count: adjustmentCount,
      };

      const categorySpending: CategorySpending[] = Array.from(spendingMap.values())
        .map((bucket) => ({
          categoryId: bucket.categoryId,
          categoryName: bucket.categoryName,
          categoryColor: bucket.categoryColor,
          amount: bucket.amount,
          percentage: totalExpense > 0 ? (bucket.amount / totalExpense) * 100 : 0,
          transactionCount: bucket.transactionCount,
          categoryIds: Array.from(bucket.categoryIds),
          isUncategorized: bucket.isUncategorized,
        }))
        .sort((a, b) => b.amount - a.amount);

      // Calculate monthly trends (EXCLUDES adjustments - same as totals)
      const monthlyMap = new Map<string, { income: number; expense: number }>();

      for (const txn of transactions) {
        // Skip adjustments - they shouldn't affect monthly trends
        const txnIsAdjustment = isAdjustment(txn.description);
        if (txnIsAdjustment) continue;
        if (txn.type !== 'income' && txn.type !== 'expense') continue;
        // Skip capital moves - same rule as the totals above
        if (capitalMoveMatcher && capitalMoveMatcher(txn.description)) continue;

        const date = normalizeDate(txn.date);
        const monthKey = getMonthKey(date);

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, { income: 0, expense: 0 });
        }

        const entry = monthlyMap.get(monthKey)!;
        if (txn.type === 'income') {
          entry.income += txn.amount;
        } else if (txn.type === 'expense') {
          entry.expense += txn.amount;
        }
      }

      const monthlyTrends = buildMonthlyTrends(monthlyMap, dateRange);

      // Days elapsed in the selected range so far (for no-spend-day insights).
      // "All Time" ranges anchor to the first transaction month, not year 2000.
      const isAllTime = dateRange.start.getFullYear() <= 2000;
      let effectiveStart = dateRange.start;
      if (isAllTime && monthlyTrends.length > 0) {
        effectiveStart = parseMonthKey(monthlyTrends[0].monthKey);
      }
      const now = new Date();
      const effectiveEnd = dateRange.end < now ? dateRange.end : now;
      const activeDays = Math.max(
        0,
        Math.floor(
          (new Date(effectiveEnd.getFullYear(), effectiveEnd.getMonth(), effectiveEnd.getDate()).getTime() -
            new Date(effectiveStart.getFullYear(), effectiveStart.getMonth(), effectiveStart.getDate()).getTime()) /
            86400000
        ) + 1
      );

      setData({
        categorySpending,
        monthlyTrends,
        incomeBreakdown,
        totalIncome,
        totalExpense,
        adjustments,
        capitalMoves: {
          expense: capitalMoveExpense,
          income: capitalMoveIncome,
          count: capitalMoveCount,
        },
        transactionCount: incomeCount + expenseCount,
        incomeCount,
        expenseCount,
        monthsCovered: monthlyTrends.length,
        largestExpense,
        weekendExpense,
        spendDays: spendDayKeys.size,
        activeDays,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load analytics';
      setError(message);
      console.error('[useAnalytics] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, capitalMoveMatcher]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Auto-refresh when transactions or categories change
  useEffect(() => {
    const unsubTransactions = dataEvents.on('transactions', fetchAnalytics);
    const unsubCategories = dataEvents.on('categories', fetchAnalytics);
    return () => {
      unsubTransactions();
      unsubCategories();
    };
  }, [fetchAnalytics]);

  return {
    data,
    loading,
    error,
    refresh: fetchAnalytics,
    dateRange,
    setDateRange,
  };
}

const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__';
const UNCATEGORIZED_COLOR = '#6f7688';

/**
 * Check if transaction is a balance adjustment.
 */
function isAdjustment(desc: string | undefined): boolean {
  if (!desc) return false;
  const lower = desc.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

function normalizeDate(date: Date | string | number): Date {
  return date instanceof Date ? date : new Date(date);
}

function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function parseMonthKey(monthKey: string): Date {
  const [year, month] = monthKey.split('-');
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function getMonthKeysInRange(startKey: string, endKey: string): string[] {
  if (startKey > endKey) return [];

  const keys: string[] = [];
  let cursor = parseMonthKey(startKey);
  const end = parseMonthKey(endKey);
  let guard = 0;

  while (cursor <= end && guard < 240) {
    keys.push(getMonthKey(cursor));
    cursor = addMonths(cursor, 1);
    guard++;
  }

  return keys;
}

/**
 * Build zero-filled monthly trends for the selected range.
 * All Time starts at the first transaction month instead of year 2000.
 */
function buildMonthlyTrends(
  monthlyMap: Map<string, { income: number; expense: number }>,
  dateRange: { start: Date; end: Date }
): MonthlyTrend[] {
  const transactionMonthKeys = Array.from(monthlyMap.keys()).sort();
  if (transactionMonthKeys.length === 0) return [];

  const selectedStartKey = getMonthKey(dateRange.start);
  const selectedEndKey = getMonthKey(dateRange.end);
  const isAllTimeRange = dateRange.start.getFullYear() <= 2000;
  const startKey = isAllTimeRange ? transactionMonthKeys[0] : selectedStartKey;
  const monthKeys = getMonthKeysInRange(startKey, selectedEndKey);
  const includeYear = new Set(monthKeys.map((key) => key.slice(0, 4))).size > 1;

  return monthKeys.map((monthKey) => {
    const values = monthlyMap.get(monthKey) || { income: 0, expense: 0 };
    const date = parseMonthKey(monthKey);

    return {
      month: formatMonthAxis(date, includeYear),
      monthKey,
      fullLabel: formatMonthYear(date),
      income: values.income,
      expense: values.expense,
      net: values.income - values.expense,
      hasData: monthlyMap.has(monthKey),
    };
  });
}
